// Cave84 — EditBottleModal
// Allows the owner to edit bottle-level fields (cost, format, fill, provenance, notes)
// and the producer's wine region (which affects region classification across inventory).

import { useState } from 'react';
import type {
  Bottle,
  BottleFormat,
  Currency,
  FillLevel,
  LabelCondition,
  PurchaseSource,
} from '@cave84/shared-types';
import type { Producer } from '@cave84/shared-types';
import { useUpdateBottle } from '@/hooks/useBottles';
import { useUpdateProducer } from '@/hooks/useProducers';
import { SOURCE_LABELS } from '@/services/bottleLabels';

// ─── Option Lists ──────────────────────────────────────────────────────────────

const FORMAT_OPTIONS: { value: BottleFormat; label: string }[] = [
  { value: 'half',          label: 'Half (375ml)' },
  { value: '750ml',         label: 'Standard (750ml)' },
  { value: 'magnum',        label: 'Magnum (1.5L)' },
  { value: 'double_magnum', label: 'Double Magnum (3L)' },
  { value: 'jeroboam',      label: 'Jeroboam (4.5L)' },
  { value: 'imperial',      label: 'Impériale (6L)' },
  { value: 'salmanazar',    label: 'Salmanazar (9L)' },
  { value: 'methuselah',    label: 'Methuselah (6L Champ.)' },
  { value: 'other',         label: 'Other' },
];

const FILL_OPTIONS: { value: FillLevel; label: string }[] = [
  { value: 'into_neck',     label: 'Into Neck' },
  { value: 'base_of_neck',  label: 'Base of Neck' },
  { value: 'top_shoulder',  label: 'Top Shoulder' },
  { value: 'upper_shoulder',label: 'Upper Shoulder' },
  { value: 'mid_shoulder',  label: 'Mid Shoulder' },
  { value: 'low_shoulder',  label: 'Low Shoulder' },
];

const CONDITION_OPTIONS: { value: LabelCondition; label: string }[] = [
  { value: 'perfect',  label: 'Perfect' },
  { value: 'minor',    label: 'Minor' },
  { value: 'scuffed',  label: 'Scuffed' },
  { value: 'torn',     label: 'Torn' },
  { value: 'stained',  label: 'Stained' },
  { value: 'missing',  label: 'Missing' },
];

const SOURCE_OPTIONS: { value: PurchaseSource; label: string }[] =
  (Object.keys(SOURCE_LABELS) as PurchaseSource[]).map(value => ({ value, label: SOURCE_LABELS[value] }));

const CURRENCY_OPTIONS: Currency[] = ['EUR', 'USD', 'ILS', 'GBP', 'CHF', 'JPY'];

// Common producer region values — maps to canonical names in useInventory.canonicalRegion
const REGION_SUGGESTIONS = [
  'Burgundy', 'Bordeaux', 'Champagne', 'Rhône Valley',
  'Loire Valley', 'Alsace', 'Provence', 'Languedoc-Roussillon',
  'Piedmont', 'Tuscany', 'Veneto', 'Sicily',
  'Rioja', 'Priorat', 'Ribera del Duero',
  'Mosel', 'Rheingau', 'Rheinhessen',
  'Barossa Valley', 'McLaren Vale', 'Napa Valley', 'Sonoma',
  'Other',
];

// ─── Field helpers ─────────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs text-text-muted uppercase tracking-wide mb-1">
      {children}
    </label>
  );
}

function FieldSelect<T extends string>({
  value, onChange, options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="w-full bg-surface-2 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-gold"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function FieldInput({
  value, onChange, type = 'text', placeholder,
}: {
  value: string | number;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-surface-2 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-gold placeholder:text-text-muted"
    />
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3 mt-5 first:mt-0">
      {children}
    </h3>
  );
}

// ─── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  bottle: Bottle;
  producer: Producer | null | undefined;
  onClose: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function EditBottleModal({ bottle, producer, onClose }: Props) {
  // — Bottle fields —
  const [format,           setFormat]           = useState<BottleFormat>(bottle.format);
  const [fillLevel,        setFillLevel]        = useState<FillLevel>(bottle.fill_level);
  const [labelCond,        setLabelCond]        = useState<LabelCondition>(bottle.label_condition);
  const [capsuleCond,      setCapsuleCond]      = useState<LabelCondition>(bottle.capsule_condition);
  const [inOWC,            setInOWC]            = useState(bottle.in_OWC);
  const [purchaseSource,   setPurchaseSource]   = useState<PurchaseSource>(bottle.purchase_source);
  const [sourceDetail,     setSourceDetail]     = useState(bottle.purchase_source_detail ?? '');
  const [cost,             setCost]             = useState(String(bottle.cost_landed));
  const [currency,         setCurrency]         = useState<Currency>(bottle.cost_currency);
  const [costEur,          setCostEur]          = useState(String(bottle.cost_eur_at_purchase));
  const [notes,            setNotes]            = useState(bottle.notes ?? '');
  const [specialOccasion,  setSpecialOccasion]  = useState(bottle.is_special_occasion);
  const [specialReason,    setSpecialReason]    = useState(bottle.special_occasion_reason ?? '');

  // — Producer region —
  const [producerRegion,   setProducerRegion]   = useState(producer?.region ?? '');
  const [showRegionList,   setShowRegionList]   = useState(false);

  const updateBottle  = useUpdateBottle();
  const updateProducer = useUpdateProducer();

  const isBusy = updateBottle.isPending || updateProducer.isPending;

  async function handleSave() {
    const patch = {
      format,
      fill_level: fillLevel,
      label_condition: labelCond,
      capsule_condition: capsuleCond,
      in_OWC: inOWC,
      purchase_source: purchaseSource,
      purchase_source_detail: sourceDetail || undefined,
      cost_landed: Number(cost),
      cost_currency: currency,
      cost_eur_at_purchase: Number(costEur),
      notes: notes || undefined,
      is_special_occasion: specialOccasion,
      special_occasion_reason: specialOccasion ? (specialReason || undefined) : undefined,
    };

    const promises: Promise<void>[] = [
      updateBottle.mutateAsync({
        id: bottle.id,
        cellarId: bottle.current_cellar_id,
        vintageId: bottle.vintage_id,
        patch,
      }),
    ];

    // Only update producer if region changed and producer is known
    if (producer && producerRegion !== producer.region) {
      promises.push(updateProducer.mutateAsync({ id: producer.id, patch: { region: producerRegion } }));
    }

    await Promise.all(promises);
    onClose();
  }

  const error = updateBottle.error ?? updateProducer.error;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Close"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-0 sm:px-4"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- dialog container; onClick only isolates the backdrop click */}
      <div
        className="bg-surface-1 w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl border border-border-medium shadow-2xl flex flex-col max-h-[90dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-medium flex-shrink-0">
          <h2 className="text-base font-semibold text-text-primary">Edit Bottle</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">

          {/* Bottle Details */}
          <SectionTitle>Bottle Details</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Format</Label>
              <FieldSelect value={format} onChange={setFormat} options={FORMAT_OPTIONS} />
            </div>
            <div>
              <Label>Fill Level</Label>
              <FieldSelect value={fillLevel} onChange={setFillLevel} options={FILL_OPTIONS} />
            </div>
            <div>
              <Label>Label Condition</Label>
              <FieldSelect value={labelCond} onChange={setLabelCond} options={CONDITION_OPTIONS} />
            </div>
            <div>
              <Label>Capsule Condition</Label>
              <FieldSelect value={capsuleCond} onChange={setCapsuleCond} options={CONDITION_OPTIONS} />
            </div>
          </div>
          <div className="pt-1">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={inOWC}
                onChange={(e) => setInOWC(e.target.checked)}
                className="w-4 h-4 accent-gold rounded"
              />
              <span className="text-sm text-text-secondary">Original Wooden Case (OWC)</span>
            </label>
          </div>

          {/* Purchase */}
          <SectionTitle>Purchase</SectionTitle>
          <div>
            <Label>Source</Label>
            <FieldSelect value={purchaseSource} onChange={setPurchaseSource} options={SOURCE_OPTIONS} />
          </div>
          <div>
            <Label>Source Detail (lot #, reference…)</Label>
            <FieldInput value={sourceDetail} onChange={setSourceDetail} placeholder="e.g. MWC Lot 342" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Purchase Price</Label>
              <FieldInput value={cost} onChange={setCost} type="number" placeholder="0" />
            </div>
            <div>
              <Label>Currency</Label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                className="w-full bg-surface-2 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-gold"
              >
                {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>EUR at Purchase</Label>
            <FieldInput value={costEur} onChange={setCostEur} type="number" placeholder="0" />
            <p className="text-xs text-text-muted mt-1">
              Used for portfolio valuation. Enter the EUR equivalent at time of purchase.
            </p>
          </div>

          {/* Notes & Special Occasion */}
          <SectionTitle>Notes & Occasion</SectionTitle>
          <div>
            <Label>Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Personal notes about this bottle…"
              className="w-full bg-surface-2 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-gold placeholder:text-text-muted resize-none"
            />
          </div>
          <div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={specialOccasion}
                onChange={(e) => setSpecialOccasion(e.target.checked)}
                className="w-4 h-4 accent-gold rounded"
              />
              <span className="text-sm text-text-secondary">🌟 Special Occasion Bottle</span>
            </label>
          </div>
          {specialOccasion && (
            <div>
              <Label>Occasion Reason</Label>
              <FieldInput value={specialReason} onChange={setSpecialReason} placeholder="e.g. 40th birthday" />
            </div>
          )}

          {/* Producer Region */}
          <SectionTitle>Wine Region</SectionTitle>
          <div className="rounded-lg bg-surface-2 border border-border-medium p-3">
            <div className="flex items-start gap-2 mb-3">
              <svg className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-text-muted leading-relaxed">
                This updates the region for <span className="text-text-secondary font-medium">{producer?.name ?? 'this producer'}</span> and will affect all their bottles in the inventory filter.
              </p>
            </div>
            <Label>Producer Region</Label>
            <div className="relative">
              <FieldInput
                value={producerRegion}
                onChange={(v) => { setProducerRegion(v); setShowRegionList(true); }}
                placeholder="e.g. Burgundy"
              />
              {showRegionList && producerRegion && (
                <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-surface-2 border border-border-medium rounded-lg overflow-hidden shadow-xl max-h-48 overflow-y-auto">
                  {REGION_SUGGESTIONS
                    .filter((r) => r.toLowerCase().includes(producerRegion.toLowerCase()) && r !== producerRegion)
                    .map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => { setProducerRegion(r); setShowRegionList(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-surface-3 hover:text-text-primary transition-colors"
                      >
                        {r}
                      </button>
                    ))
                  }
                </div>
              )}
            </div>

            {/* Quick-select chips */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {['Burgundy', 'Bordeaux', 'Champagne', 'Rhône Valley', 'Piedmont', 'Tuscany'].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => { setProducerRegion(r); setShowRegionList(false); }}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                    producerRegion === r
                      ? 'bg-gold/20 border-gold/50 text-gold'
                      : 'bg-surface-3 border-border-medium text-text-muted hover:border-gold/40 hover:text-text-secondary'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-4 border-t border-border-medium flex-shrink-0 space-y-3">
          {error && (
            <p className="text-xs text-red-400 text-center">{(error as Error).message}</p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isBusy}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gold text-bg text-sm font-semibold hover:bg-gold/90 transition-colors disabled:opacity-40"
            >
              {isBusy ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
